import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, ShoppingBag } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

/**
 * FrequentlyBoughtTogether
 * Shows main product + up to 2 curated/suggested pieces with selectable checkboxes,
 * a combined-total ribbon, and a single "Add selected to bag" CTA.
 */
const FrequentlyBoughtTogether = ({ slug }) => {
  const [main, setMain] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({}); // { productId: bool }
  const { addItem } = useCart();

  useEffect(() => {
    let mounted = true;
    api.get(`/products/${slug}/bundle`).then((r) => {
      if (!mounted) return;
      setMain(r.data.main);
      setSuggestions(r.data.suggestions || []);
      // Default selection: main + all suggestions
      const init = { [r.data.main.id]: true };
      (r.data.suggestions || []).forEach((p) => { init[p.id] = true; });
      setSelected(init);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [slug]);

  const all = useMemo(() => (main ? [main, ...suggestions] : []), [main, suggestions]);
  const chosen = useMemo(() => all.filter((p) => selected[p.id]), [all, selected]);
  const total = chosen.reduce((s, p) => s + (p.price || 0), 0);
  const originalTotal = chosen.reduce((s, p) => s + (p.compare_at_price || p.price || 0), 0);
  const savings = originalTotal - total;

  if (!main || suggestions.length === 0) return null;

  const toggle = (id) => {
    if (id === main.id) return; // main always required
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const addAll = () => {
    if (chosen.length === 0) return;
    chosen.forEach((p) => addItem(p, 1, (p.sizes || [])[0] || null));
    toast.success(`${chosen.length} pieces added to your bag`);
  };

  return (
    <section className="py-14 md:py-20 bg-[#FBF7F4]" data-testid="fbt-section">
      <div className="rieave-container">
        <Reveal>
          <div className="text-center max-w-xl mx-auto mb-10">
            <p className="eyebrow mb-3">Completes the look</p>
            <h2 className="font-hero text-3xl md:text-5xl text-charcoal">
              Frequently bought <span className="serif-italic text-rose">together</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-charcoal/65">
              Bundle and save — customers who bought this piece loved these too.
            </p>
          </div>
        </Reveal>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr,auto] gap-8 items-center">
          {/* Product chain */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-2">
            {all.map((p, i) => (
              <React.Fragment key={p.id}>
                {i > 0 && (
                  <div className="self-center text-mauve flex-shrink-0" aria-hidden>
                    <span className="hidden sm:inline-flex w-8 h-8 rounded-full bg-white border border-border items-center justify-center">
                      <Plus size={14} />
                    </span>
                    <span className="sm:hidden h-[1px] w-full bg-border/80 my-1 block" />
                  </div>
                )}
                <FbtCard
                  product={p}
                  isMain={p.id === main.id}
                  checked={!!selected[p.id]}
                  onToggle={() => toggle(p.id)}
                />
              </React.Fragment>
            ))}
          </div>

          {/* Total + CTA */}
          <motion.div
            className="bg-white rounded-2xl border border-border/60 p-6 lg:min-w-[260px] shadow-sm"
            layout
            data-testid="fbt-summary"
          >
            <p className="eyebrow mb-2">Bundle total</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-hero text-3xl md:text-4xl text-charcoal" data-testid="fbt-total">{formatINR(total)}</span>
              {savings > 0 && (
                <span className="font-body text-sm text-mauve line-through">{formatINR(originalTotal)}</span>
              )}
            </div>
            {savings > 0 && (
              <p className="font-label text-[11px] uppercase tracking-[0.2em] text-rose mb-3" data-testid="fbt-savings">
                You save {formatINR(savings)}
              </p>
            )}
            <p className="text-xs text-mauve mb-5">
              {chosen.length} of {all.length} selected
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={addAll}
              disabled={chosen.length === 0}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="fbt-add-all"
            >
              <ShoppingBag size={14} />
              Add {chosen.length === 1 ? "piece" : `${chosen.length} pieces`} to bag
            </motion.button>
            <p className="text-[11px] text-center text-mauve mt-3">Ships together · free gift wrap included</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FbtCard = ({ product, isMain, checked, onToggle }) => (
  <motion.div
    whileHover={{ y: -2 }}
    className={`relative flex-1 min-w-[150px] max-w-[220px] bg-white border rounded-2xl overflow-hidden transition-all ${
      checked ? "border-rose/70 shadow-md" : "border-border/60"
    }`}
    data-testid={`fbt-card-${product.slug}`}
  >
    <button
      type="button"
      onClick={onToggle}
      disabled={isMain}
      className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${
        checked ? "bg-rose border-rose text-white" : "bg-white border-border text-transparent"
      } ${isMain ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`}
      aria-label={checked ? "Remove from bundle" : "Add to bundle"}
      data-testid={`fbt-toggle-${product.slug}`}
    >
      <Check size={13} strokeWidth={3} />
    </button>
    {isMain && (
      <span className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-charcoal text-cream text-[9px] uppercase tracking-[0.22em] rounded-full">
        This piece
      </span>
    )}
    <Link to={`/product/${product.slug}`} className="block">
      <div className="aspect-square bg-blush/40 overflow-hidden">
        {product.images?.[0] && (
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.04]" />
        )}
      </div>
      <div className="p-3">
        <p className="eyebrow mb-0.5 text-[10px]">{product.category_slug}</p>
        <h3 className="font-cormorant text-base leading-tight text-charcoal line-clamp-2 min-h-[40px]">{product.name}</h3>
        <p className="font-body text-sm text-charcoal mt-1">{formatINR(product.price)}</p>
      </div>
    </Link>
  </motion.div>
);

export default FrequentlyBoughtTogether;
